import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { searchPeople, searchPeopleQuery, resetData } from './model';
import type { People } from './types';

export const PeopleSearchControl = withControl<{ placeholder?: string }>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    placeholder,
  }) => {
    const { data, pending } = useUnit(searchPeopleQuery);

    useEffect(() => () => resetData(), []);

    const handleInputChange = (_: unknown, value: string, reason: string) => {
      if (reason === 'input') {
        searchPeople(value);
      }

      return resetData();
    };

    return (
      <FormControl disabled={disabled}>
        {label && (
          <FormLabel required={!readOnly && required} error={isError}>
            {label}
          </FormLabel>
        )}
        <Autocomplete
          loading={pending}
          value={value ?? null}
          options={Array.isArray(data) ? data : []}
          onChange={(
            _: unknown,
            newValue: string | People | (string | People)[] | null
          ) => {
            setValue(newValue || null);
          }}
          onInputChange={handleInputChange}
          ListboxComponent={Listbox}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                placeholder,
                readOnly,
                error: isError,
                endAdornment: readOnly ? null : (
                  <>
                    {pending && <CircularProgress size={20} color="inherit" />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
